import { parseJWT, isTokenValid } from '@utils/authUtils.js';
import { axios } from '@boot/axios.js';
import * as signalR from '@microsoft/signalr';
import { reportExceptionToSentry } from '@utils/sentryUtils';

// global connection object
let connection = {};
// signalr endpoint to use for hub connection builder
let signalREndpointUrl = null;

// Constants for retry logic
const MAX_RETRIES = 3;
const RETRIABLE_STATUS_CODES = new Set([500, 502, 503, 504]);

/**
 * Handle establish connection with SignalR.
 */
async function establishConnection() {
    // handle negotiation
    try {
        console.log('negotiating...');
        await negotiate();
    } catch (error) {
        console.error(`Negotiation failed due to: ${error}`);
        throw new Error('Negotiation failed');
    }

    // handle start connection
    try {
        await connection.start();
        console.log('SignalR connection established');
    } catch (error) {
        console.error(`Start connection failed due to: ${error}`);
        throw new Error('Start connection failed');
    }
}

/**
 * Get access token to use for SignalR connection.
 * Note: This function also sets the signalr endpoint url to be used.
 * @return {String} - Access token.
 */
async function getSignalRAccessToken() {
    let signalRAccessToken = null;
    await axios
        .post('stream/connect/', null)
        .then(response => {
            signalRAccessToken = response.data['access_token'];
            signalREndpointUrl = response.data['url'];
        })
        .catch(error => {
            console.error(
                `Failed to connect to stream due to: ${JSON.stringify(error)}`
            );
            reportExceptionToSentry(error);
        });
    return signalRAccessToken;
}

/**
 * Handle negotiation with signalr.
 */
async function negotiate() {
    // set signalr url + obtain first token
    let signalRAccessToken = await getSignalRAccessToken();

    // initialize connection
    connection = new signalR.HubConnectionBuilder()
        .withUrl(signalREndpointUrl, {
            // callback should return access token (String)
            accessTokenFactory: async () => {
                if (
                    signalRAccessToken !== null &&
                    isTokenValid(parseJWT(signalRAccessToken))
                ) {
                    return signalRAccessToken;
                } else {
                    signalRAccessToken = await getSignalRAccessToken();
                    return signalRAccessToken;
                }
            }
        })
        // attempt to reconnect every second
        .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: retryContext => {
                return 500;
            }
        })
        .configureLogging(signalR.LogLevel.Error)
        .build();
}

/**
 * Check if HubConnection object has been initialized.
 * If connection has been initialized (once .build() method is called on connection object, it should have an 'id' field)
 * @return {Boolean}
 */
function isInitialized() {
    return 'connectionId' in connection;
}

/**
 * Register a handler with the connection object.
 * @param {Object}
 * @param {String} name - Name of event from SignalR that triggers handler.
 * @param {Function} handler - Function to execute.
 */
function addHandler({ name, handler }) {
    console.debug(`Add SignalR handler: ${name}`);
    if (isInitialized()) {
        connection.on(name, handler);
    }
}

/**
 * Unregister a handler with the connection object.
 * @param {Object}
 * @param {String} name - Name of event from SignalR that triggers handler.
 */
function removeHandler(name) {
    console.debug(`Remove SignalR handler: ${name}`);
    if (isInitialized()) {
        connection.off(name);
    }
}

/**
 * Subscribe to an asset.
 * This function will retry up to MAX_RETRIES times if the subscription fails.
 * @param {(Number|String)} assetId
 */
async function subscribe(assetId) {
    let attempt = 0;
    while (attempt < MAX_RETRIES) {
        try {
            await axios.post('stream/subscribe/', {
                asset_id: parseInt(assetId)
            });
            console.info(`Subscribed to asset with id: ${assetId}`);
            return;
        } catch (error) {
            const status = error?.response?.status;
            attempt++;
            if (
                status &&
                RETRIABLE_STATUS_CODES.has(status) &&
                attempt < MAX_RETRIES
            ) {
                const delay = getReconnectDelay(attempt);
                console.warn(
                    `Attempt ${attempt} failed with ${status}. Retrying in ${delay}ms...`
                );
                await sleep(delay);
                continue;
            }
            console.error(
                `Failed to subscribe to asset due to: ${JSON.stringify(error)}`
            );
            reportExceptionToSentry(error);
            throw error;
        }
    }
}

/**
 * Unsubscribe to an asset.
 * @param {(Number|String)} assetId
 */
async function unsubscribe(assetId) {
    await axios
        .post('stream/unsubscribe/', {
            asset_id: parseInt(assetId)
        })

        .then(response => {
            console.info(`Unsubscribed to asset with id: ${assetId}`);
        })
        .catch(error => {
            console.error(
                `Failed to unsubscribe to asset due to: ${JSON.stringify(error)}`
            );
            reportExceptionToSentry(error);
        });
}

/**
 * Subscribe to a facility.
 * This function will retry up to MAX_RETRIES times if the subscription fails.
 * @param {(Number|String)} facilityId
 */
async function subscribeToFacility(facilityId) {
    let attempt = 0;
    while (attempt < MAX_RETRIES) {
        try {
            await axios.post('stream/subscribe/', {
                facility_id: parseInt(facilityId)
            });
            console.info(`Subscribed to facility with id: ${facilityId}`);
            return;
        } catch (error) {
            const status = error?.response?.status;
            attempt++;
            if (
                status &&
                RETRIABLE_STATUS_CODES.has(status) &&
                attempt < MAX_RETRIES
            ) {
                const delay = getReconnectDelay(attempt);
                console.warn(
                    `Attempt ${attempt} failed with ${status}. Retrying in ${delay}ms...`
                );
                await sleep(delay);
                continue;
            }
            console.error(
                `Failed to subscribe to facility due to: ${JSON.stringify(error)}`
            );
            reportExceptionToSentry(error);
            throw error;
        }
    }
}

/**
 * Unsubscribe to a facility.
 * @param {(Number|String)} facilityId
 */
async function unsubscribeFromFacility(facilityId) {
    await axios
        .post('stream/unsubscribe/', {
            facility_id: parseInt(facilityId)
        })

        .then(response => {
            console.info(`Unsubscribed to facility with id: ${facilityId}`);
        })
        .catch(error => {
            console.error(
                `Failed to unsubscribe to facility due to: ${JSON.stringify(error)}`
            );
            reportExceptionToSentry(error);
        });
}

/**
 * Determine connection status of hub instance.
 * Note: This will also return false if hub instance has not been set yet.
 * @return {Boolean}
 */
function isConnected() {
    // check if hub instance has been set
    if (isInitialized()) {
        // if set, check if connection state is connected (@aspnet\signalr enumerates this status as 1)
        return connection.state === 'Connected';
    }
    return false;
}

/**
 * Get connection object.
 * @return {Object} - HubConnection instance.
 */
function getConnectionObject() {
    return connection;
}

/**
 * Handle closing a connection.
 */
async function close() {
    // check if hub instance has been set
    if (isInitialized()) {
        await connection.stop();
    }
}

/**
 * Set on close callback.
 * @param {Function} - Function to execute when the connection closes.
 */
async function onClose(callback) {
    connection.onclose(async () => {
        await callback();
    });
}

/**
 * Sets a callback for when the HubConnection attempts to reconnect.
 * @param {Function}
 */
async function onReconnecting(callback) {
    connection.onreconnecting(async () => {
        await callback();
    });
}

/**
 * Sets a callback for when the HubConnection successfully reconnects.
 * @param {Function}
 */
async function onReconnected(callback) {
    connection.onreconnected(async () => {
        await callback();
    });
}

/**
 * Destroy connection object.
 */
async function destroy() {
    // set connection obj to new object to lose all references to previous connection object
    if (isInitialized()) {
        await connection.stop();
    }
    connection = {};
}

/**
 * Sleeps for the specified duration.
 * @param {number} ms - Milliseconds to sleep.
 * @returns {Promise<void>} Resolves after the specified delay.
 */
function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Calculates the delay for the specified reconnect attempt.
 * @param {number} attempt - The number of the reconnect attempt.
 * @returns {number} The delay in milliseconds.
 */
function getReconnectDelay(attempt) {
    return Math.min(2000, 500 * 2 ** (attempt - 1));
}

export default {
    establishConnection,
    isConnected,
    addHandler,
    removeHandler,
    subscribe,
    unsubscribe,
    subscribeToFacility,
    unsubscribeFromFacility,
    close,
    getConnectionObject,
    isInitialized,
    onClose,
    onReconnecting,
    onReconnected,
    negotiate,
    destroy,
    getSignalRAccessToken,
    getReconnectDelay
};
